import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MonitorSmartphone, Share2, MessageSquarePlus, Sparkles, ArrowRight, ArrowLeft, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { BrandLogo } from "@/components/BrandLogo";

const isFrench = () => (navigator.language || navigator.userLanguage || "en").toLowerCase().startsWith("fr");

const STEPS = {
  en: {
    skip: "Skip", back: "Back", next: "Next", done: "Got it", stepOf: (a, b) => `Step ${a} of ${b}`,
    steps: [
      { icon: Sparkles, title: "Your website preview is ready", body: "This is a live preview of your website. Here's a quick 20-second guide on how to look around and share your thoughts." },
      { icon: MonitorSmartphone, title: "See it on any device", body: "Use the Desktop and Mobile buttons at the top to check how your site looks on a computer and on a phone." },
      { icon: MessageSquarePlus, title: "Leave feedback anywhere", body: "Spot something you'd like changed? Click Comment, then tap the exact spot on the page to pin a note right where you mean it." },
      { icon: Share2, title: "Your notes reach your designer", body: "Every comment is saved and emailed straight to your web designer the moment you post it — no account or email needed on your end. Add as many as you like." },
    ],
  },
  fr: {
    skip: "Passer", back: "Retour", next: "Suivant", done: "Compris", stepOf: (a, b) => `Étape ${a} sur ${b}`,
    steps: [
      { icon: Sparkles, title: "L'aperçu de votre site est prêt", body: "Ceci est un aperçu en direct de votre site web. Voici un petit guide de 20 secondes pour l'explorer et donner votre avis." },
      { icon: MonitorSmartphone, title: "Voyez-le sur tout appareil", body: "Utilisez les boutons Ordinateur et Mobile en haut pour voir le rendu de votre site sur un ordinateur et sur un téléphone." },
      { icon: MessageSquarePlus, title: "Laissez un commentaire n'importe où", body: "Un détail à modifier ? Cliquez sur Commenter, puis touchez l'endroit précis de la page pour y épingler une note." },
      { icon: Share2, title: "Vos notes vont à votre concepteur", body: "Chaque commentaire est enregistré et envoyé par e-mail à votre concepteur dès que vous le publiez — sans compte ni e-mail de votre côté. Ajoutez-en autant que vous voulez." },
    ],
  },
};

export const Tutorial = ({ onClose }) => {
  const t = STEPS[isFrench() ? "fr" : "en"];
  const [i, setI] = useState(0);
  const step = t.steps[i];
  const Icon = step.icon;
  const last = i === t.steps.length - 1;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" data-testid="tutorial-overlay">
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="absolute inset-0 bg-black/80 backdrop-blur-md" onClick={onClose}
      />
      <motion.div
        initial={{ opacity: 0, scale: 0.96, y: 12 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ type: "spring", stiffness: 260, damping: 24 }}
        className="relative w-full max-w-md rounded-2xl border border-white/10 bg-[#0c0c0e] p-7 shadow-2xl"
        data-testid="tutorial-card"
      >
        <div className="flex items-center justify-between mb-6">
          <BrandLogo size="sm" />
          <button onClick={onClose} className="text-white/40 hover:text-white transition-colors" data-testid="tutorial-skip-x">
            <X className="h-4 w-4" />
          </button>
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={i}
            initial={{ opacity: 0, x: 16 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -16 }}
            transition={{ duration: 0.2 }}
          >
            <div className="h-14 w-14 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center mb-5">
              <Icon className="h-6 w-6" style={{ color: "#2f7bf6" }} />
            </div>
            <h2 className="font-display text-2xl font-semibold text-white tracking-tight">{step.title}</h2>
            <p className="mt-3 text-sm text-white/60 leading-relaxed">{step.body}</p>
          </motion.div>
        </AnimatePresence>

        <div className="mt-7 flex items-center gap-2" data-testid="tutorial-dots">
          {t.steps.map((_, idx) => (
            <span key={idx} className={`h-1.5 rounded-full transition-all ${idx === i ? "w-6 bg-white" : "w-1.5 bg-white/25"}`} />
          ))}
        </div>

        <div className="mt-6 flex items-center justify-between">
          <button
            onClick={i === 0 ? onClose : () => setI(i - 1)}
            className="text-sm text-white/50 hover:text-white transition-colors inline-flex items-center gap-1"
            data-testid="tutorial-back"
          >
            {i === 0 ? t.skip : <><ArrowLeft className="h-4 w-4" /> {t.back}</>}
          </button>
          <span className="text-xs text-white/30">{t.stepOf(i + 1, t.steps.length)}</span>
          <Button
            onClick={last ? onClose : () => setI(i + 1)}
            className="bg-white text-black hover:bg-zinc-200 gap-1.5 rounded-lg h-9 px-4 font-medium"
            data-testid="tutorial-next"
          >
            {last ? t.done : t.next} <ArrowRight className="h-4 w-4" />
          </Button>
        </div>
      </motion.div>
    </div>
  );
};
